import { z } from "zod";
import { attributionSourceSchema, environmentSchema, hierarchySchema } from "./codes.js";
import { amountSchema, labelSchema, monthSchema, timestampSchema } from "./primitives.js";
/** A month with billing data. */
export const periodSchema = z.object({
    month: monthSchema,
    currency: z.string(),
    totalAmount: amountSchema,
    fetchedAt: timestampSchema,
    closed: z.boolean(),
});
export const periodsResponseSchema = z.object({ periods: z.array(periodSchema) });
/** Environment filter value: an environment, or resources with no environment. */
export const environmentFilterSchema = z.union([environmentSchema, z.literal("UNASSIGNED")]);
const listParameter = (item) => z
    .union([item, z.array(item)])
    .optional()
    .transform((value) => (value === undefined ? undefined : Array.isArray(value) ? value : [value]));
/** A month range, inclusive at both ends. */
const monthRangeQuerySchema = z
    .object({ from: monthSchema, to: monthSchema })
    .refine((query) => query.from <= query.to, { message: "from must not be after to", path: ["to"] });
export const costTreeQuerySchema = z
    .object({
    from: monthSchema,
    to: monthSchema,
    hierarchy: hierarchySchema.default("SERVICE_TENANT"),
    environment: listParameter(environmentFilterSchema),
})
    .refine((query) => query.from <= query.to, { message: "from must not be after to", path: ["to"] });
export const COST_TREE_NODE_KINDS = ["ROOT", "TENANT", "SERVICE", "CATEGORY", "RESOURCE"];
export const costTreeNodeSchema = z.lazy(() => z.object({
    kind: z.enum(COST_TREE_NODE_KINDS),
    code: z.string().nullable(),
    label: z.string().nullable(),
    cloudResourceId: z.uuid().optional(),
    amount: amountSchema,
    previousAmount: amountSchema,
    children: z.array(costTreeNodeSchema),
}));
export const costTreeResponseSchema = z.object({
    from: monthSchema,
    to: monthSchema,
    previousFrom: monthSchema,
    previousTo: monthSchema,
    currency: z.string().nullable(),
    root: costTreeNodeSchema,
});
/** The resolved attribution of a resource in a month. */
export const attributionSchema = z.object({
    tenant: z.string().nullable(),
    tenantSource: attributionSourceSchema,
    service: z.string().nullable(),
    serviceSource: attributionSourceSchema,
    environment: environmentSchema.nullable(),
    environmentSource: attributionSourceSchema,
});
export const resourceListQuerySchema = monthRangeQuerySchema.and(z.object({
    unassignedOnly: z
        .enum(["true", "false"])
        .optional()
        .transform((value) => value === "true"),
}));
export const resourceSummarySchema = z.object({
    cloudResourceId: z.uuid(),
    resourceId: z.string(),
    category: z.string(),
    type: z.string(),
    name: z.string().nullable(),
    zone: z.string().nullable(),
    amount: amountSchema,
    attribution: attributionSchema,
});
export const resourceListResponseSchema = z.object({
    resources: z.array(resourceSummarySchema),
});
export const resourceCostDetailSchema = z.object({
    plan: z.string().nullable(),
    zone: z.string().nullable(),
    hours: z.number(),
    amount: amountSchema,
    labels: z.array(labelSchema),
    attributes: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
});
export const resourceMonthSchema = z.object({
    month: monthSchema,
    amount: amountSchema,
    hours: z.number(),
    details: z.array(resourceCostDetailSchema),
    attribution: attributionSchema,
});
export const resourceReferenceSchema = z.object({
    cloudResourceId: z.uuid(),
    resourceId: z.string(),
    name: z.string().nullable(),
});
export const resourceDetailSchema = z.object({
    cloudResourceId: z.uuid(),
    resourceId: z.string(),
    category: z.string(),
    type: z.string(),
    zone: z.string().nullable(),
    name: z.string().nullable(),
    hostname: z.string().nullable(),
    kubernetesCluster: z.string().nullable(),
    labels: z.array(labelSchema),
    parent: resourceReferenceSchema.nullable(),
    months: z.array(resourceMonthSchema),
});
//# sourceMappingURL=costs.js.map